import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, ButtonBase, Checkbox, CircularProgress, Drawer, FormControlLabel, IconButton, InputBase, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { AccountBalanceOutlined, AttachFile, DescriptionOutlined, EventNoteOutlined, HeadsetMic, History, MenuBookOutlined, Mic, OpenInNew, PieChartOutline } from "@mui/icons-material";
import { ActionChips, AreaHeader, AssistantNote, AuditNotice, ComposerFrame, ComposerIconButton, conversationSx, FocusBar, listColumnSx, HeaderBadge, HeaderLink, SendButton, SpeakerLabel, TopicList, VoiceStatus, WriterTag } from "../components/assistants/AssistantUi";
import ProposalCard from "../components/assistants/ProposalCard";
import StatementCard, { StatementCardState } from "../components/assistants/StatementCard";
import { useDictation } from "../components/assistants/useDictation";
import { useSpeechReply } from "../components/assistants/useSpeechReply";
import { voiceService } from "../services/voiceService";
import { parseAssistant, renderMarkdown } from "../components/chat/AgentConversation";
import { useAuth } from "../contexts/AuthContext";
import { openPageInNewTab } from "../contexts/RouterContext";
import { colors } from "../theme/designSystem";
import { aiChatService, ChatMessage, ConversationSummary, SendResult } from "../services/aiChatService";
import { ConversationHistory } from "../components/assistants/ConversationHistory";
import { assistantsService, MyAssistantArea, ProposalCardData, StatementAccount, StatementReview } from "../services/assistantsService";

// Área Livro-caixa dos Assistentes (item 228, F3a — RN-571): a pessoa anexa o extrato, diz de qual conta é e recebe
// a conferência (prova de saldo + confronto com o livro). O arquivo vai direto à rota de leitura e não passa pela IA;
// a conversa ao lado é a do agente de consulta da área. Molde no canvas "Super Assistente · Livro-caixa" (28/10).

interface Props {
  area: MyAssistantArea;
}

type Item =
  | { kind: "msg"; role: "user" | "assistant"; content: string; agentName?: string; agentId?: string | null; proposals?: ProposalCardData[] | null }
  | { kind: "file"; id: number; name: string; size: number }
  | { kind: "pick"; id: number; file: File; remember?: boolean }
  | { kind: "statement"; id: number; state: StatementCardState; file?: File };

const ACCEPT = ".pdf,.csv,.txt,.xlsx,.xls";

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { description?: string } } })?.response?.data?.description || fallback;

const sizeLabel = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`);

/** A fala que acompanha o cartão: o que a pessoa precisa saber antes de abrir os detalhes. */
const summaryOf = (r: StatementReview): string => {
  if (r.proof === "BROKEN") return "Não consegui conferir este extrato: o saldo não fecha. Detalhes no cartão.";
  if (r.proof === "NONE") return "O arquivo não traz o saldo, então preciso que você confira os totais antes de seguir.";
  if (r.newCount === 0 && r.onlyInBook.length === 0) return "Os saldos conferem e todos os movimentos já estão no livro.";
  const parts = [
    `${r.proof === "CLOSED" ? "Os saldos conferem do começo ao fim" : "Os totais conferem"}.`,
    r.newCount > 0 ? `${r.newCount} ${r.newCount === 1 ? "movimento ainda não está" : "movimentos ainda não estão"} no livro.` : "",
    r.onlyInBook.length > 0
      ? `${r.onlyInBook.length} ${r.onlyInBook.length === 1 ? "lançamento do livro não aparece" : "lançamentos do livro não aparecem"} no extrato — vale conferir o valor.`
      : "",
  ];
  return parts.filter(Boolean).join(" ");
};

const fromServer = (m: ChatMessage): Item => ({
  kind: "msg", role: m.role === "user" ? "user" : "assistant", content: m.content, agentName: m.agentName ?? undefined,
  agentId: m.agentId, proposals: m.proposals,
});

const CashBookAssistant: React.FC<Props> = ({ area }) => {
  const { user } = useAuth();
  const isMobile = useMediaQuery("(max-width:767.98px)");
  const [items, setItems] = useState<Item[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<StatementAccount[] | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const seq = useRef(0);
  const [busyCard, setBusyCard] = useState<string | null>(null);
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  // Modo conversa (RN-570, D-OP-34): ouve → envia na pausa → fala a resposta → volta a ouvir
  const [voiceReady, setVoiceReady] = useState(false);
  const [conversation, setConversation] = useState(false);
  const conversationRef = useRef(false);
  const speech = useSpeechReply(area.areaKey, setError);
  const dictation = useDictation(draft, setDraft, setError, conversation ? (text) => { void onVoice(text); } : undefined);
  const userInitial = (user?.email || "V").trim().charAt(0).toUpperCase();

  useEffect(() => {
    if (!area.speechReply) {
      setVoiceReady(false);
      return;
    }
    let alive = true;
    voiceService.getSettings()
      .then((v) => { if (alive) setVoiceReady(v.speechReady); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [area.speechReply]);

  const toggleConversation = () => {
    if (conversationRef.current) {
      conversationRef.current = false;
      setConversation(false);
      speech.stop();
      dictation.cancel();
      return;
    }
    speech.unlock();
    conversationRef.current = true;
    setConversation(true);
    dictation.start(draft);
  };

  // Microfone durante a fala: interrompe a voz e passa a ouvir
  const handleMic = () => {
    if (speech.speaking) {
      speech.stop();
      dictation.start("");
      return;
    }
    dictation.toggle();
  };

  /** No modo conversa, fala a resposta e volta a ouvir; fora dele, não faz nada. */
  const speakThenListen = async (reply: string) => {
    if (!conversationRef.current) return;
    const spoken = parseAssistant(reply).text;
    if (spoken) await speech.speakBrief(spoken);
    if (conversationRef.current) dictation.start("");
  };

  // Fala no modo conversa: "sim"/"não" respondem ao "Quer que eu leia o restante?"; o resto é pedido novo
  const onVoice = async (text: string) => {
    const answer = await speech.answerFollowUp(text);
    if (answer === null) {
      await send(text);
      return;
    }
    setDraft("");
    if (conversationRef.current) dictation.start("");
  };

  useEffect(() => {
    let alive = true;
    assistantsService.statementAccounts()
      .then((a) => { if (alive) setAccounts(a); })
      .catch(() => { if (alive) setAccounts([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [items.length, sending]);

  /** Falas da resposta: a do agente de consulta (vazia quando a pessoa respondeu ao de alteração) e a do de alteração, com os cartões. */
  const repliesOf = (res: SendResult): Item[] => [
    ...(res.reply
      ? [{ kind: "msg" as const, role: "assistant" as const, content: res.reply, agentName: area.readAgentName, agentId: area.readAgentId,
          proposals: res.proposals ?? null }]
      : []),
    ...(res.handoff
      ? [{ kind: "msg" as const, role: "assistant" as const, content: res.handoff.content, agentId: res.handoff.agentId,
          agentName: res.handoff.agentName ?? area.writeAgentName ?? undefined, proposals: res.handoff.proposals }]
      : []),
  ];
  const spokenOf = (res: SendResult) => [res.reply, res.handoff?.content ?? ""].filter(Boolean).join("\n");

  // O estado do cartão vem sempre do servidor: depois de cada decisão, os cartões são relidos da conversa
  const refreshCards = async () => {
    if (!conversationId) return;
    try {
      const fresh = new Map((await aiChatService.messages(conversationId)).flatMap((m) => m.proposals ?? []).map((c) => [c.id, c] as const));
      setItems((list) => list.map((it) => (it.kind === "msg" && it.proposals
        ? { ...it, proposals: it.proposals.map((c) => fresh.get(c.id) ?? c) }
        : it)));
    } catch {
      // Mantém o que está na tela; a próxima ação relê de novo
    }
  };

  const decide = async (card: ProposalCardData, action: "confirm" | "discard" | "undo") => {
    setBusyCard(card.id);
    setCardErrors(({ [card.id]: _, ...rest }) => rest);
    try {
      await assistantsService[action](card.id);
    } catch (e) {
      setCardErrors((m) => ({ ...m, [card.id]: errorText(e, "Não foi possível concluir. Tente de novo.") }));
    } finally {
      await refreshCards();
      setBusyCard(null);
    }
  };

  const replace = (id: number, next: Item | null) =>
    setItems((list) => list.flatMap((it) => ("id" in it && it.id === id ? (next ? [next] : []) : [it])));

  // Sem conta = o servidor reconhece pelo extrato (RN-574); não reconhecida, a pessoa escolhe numa lista sem nada marcado
  const review = async (id: number, file: File, account?: StatementAccount, remember = false) => {
    replace(id, { kind: "statement", id, file, state: { kind: "loading", fileName: file.name, accountTitle: account?.title ?? "Identificando a conta…" } });
    let r: StatementReview;
    try {
      const res = await assistantsService.reviewStatement(file, account?.id, remember);
      if (res.needsAccount || !res.review) {
        replace(id, { kind: "pick", id, file });
        return;
      }
      r = res.review;
      replace(id, { kind: "statement", id, file, state: { kind: "review", fileName: file.name, review: r, recognizedBy: account ? null : res.recognizedBy } });
    } catch (e) {
      replace(id, { kind: "statement", id, file, state: { kind: "refused", fileName: file.name, message: errorText(e, "Não consegui ler o arquivo. Tente de novo.") } });
      return;
    }
    // O agente comenta a conferência lendo o resultado pela ferramenta da área; o pedido não aparece na conversa
    // (o anexo já representa a pessoa). Sem resposta da IA, fica o resumo do próprio sistema.
    setSending(true);
    try {
      const res = await aiChatService.send(area.readAgentId, conversationId,
        `Enviei o extrato "${file.name}" da conta ${r.accountTitle}. Comente a conferência.`,
        "Super Assistente · Livro-caixa", null, area.areaKey);
      setConversationId(res.conversationId);
      setItems((list) => [...list, ...repliesOf(res)]);
      setSending(false);
      await speakThenListen(spokenOf(res));
    } catch {
      setItems((list) => [...list, { kind: "msg", role: "assistant", content: summaryOf(r), agentName: area.readAgentName }]);
      setSending(false);
    }
  };

  const attach = (file: File) => {
    setError(null);
    if (accounts !== null && accounts.length === 0) {
      setError("Nenhuma conta bancária cadastrada. Cadastre em Financeiro › Contas bancárias e envie o extrato de novo.");
      return;
    }
    const id = ++seq.current;
    setItems((list) => [...list, { kind: "file", id: -id, name: file.name, size: file.size },
      { kind: "statement", id, file, state: { kind: "loading", fileName: file.name, accountTitle: "Identificando a conta…" } }]);
    void review(id, file);
  };

  const onFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (file) attach(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async (text?: string) => {
    const message = (text ?? draft).trim();
    if (!message || sending) return;
    dictation.cancel();
    speech.stop();
    setSending(true);
    setError(null);
    setDraft("");
    setItems((list) => [...list, { kind: "msg", role: "user", content: message }]);
    let reply = "";
    try {
      const res = await aiChatService.send(area.readAgentId, conversationId, message, "Super Assistente · Livro-caixa", null, area.areaKey);
      setConversationId(res.conversationId);
      reply = spokenOf(res);
      setItems((list) => [...list, ...repliesOf(res)]);
    } catch (e) {
      setItems((list) => list.slice(0, -1));
      setDraft(message);
      setError(errorText(e, "A IA não respondeu. Tente de novo."));
    } finally {
      setSending(false);
    }
    if (reply) await speakThenListen(reply);
  };

  const openFilePicker = () => fileRef.current?.click();
  const topics = [
    { title: "Conferir um extrato", does: "PDF, CSV ou Excel do banco", icon: <DescriptionOutlined />, onClick: openFilePicker },
    { title: "Saldo das contas", does: "pelos registros do livro, no mês", icon: <AccountBalanceOutlined />, onClick: () => send("Qual o saldo das contas pelos registros do livro neste mês?") },
    { title: "Contas a vencer", does: "provisões em aberto a pagar e a receber", icon: <EventNoteOutlined />, onClick: () => send("Quais provisões estão em aberto para os próximos 30 dias?") },
    { title: "Gastos do mês", does: "saídas somadas por categoria", icon: <PieChartOutline />, onClick: () => send("Quanto saiu este mês, somado por categoria?") },
  ];

  // Histórico (RN-573): conversa reaberta mostra as falas; o cartão do extrato não volta (o arquivo foi descartado)
  const [historyOpen, setHistoryOpen] = useState(false);
  const openConversation = async (c: ConversationSummary) => {
    speech.stop();
    dictation.cancel();
    setHistoryOpen(false);
    setError(null);
    setDraft("");
    setConversationId(c.id);
    try {
      setItems((await aiChatService.messages(c.id)).map(fromServer));
    } catch {
      setItems([]);
      setError("Não foi possível abrir essa conversa.");
    }
  };
  const newConversation = () => {
    speech.stop();
    dictation.cancel();
    setHistoryOpen(false);
    setError(null);
    setConversationId(null);
    setItems([]);
  };
  const history = (
    <ConversationHistory agentId={area.readAgentId} area={area.areaKey} activeId={conversationId}
      reloadKey={`${conversationId}:${items.length}`}
      onOpen={openConversation} onNew={newConversation} />
  );

  const header = isMobile ? (
    <FocusBar
      avatar={<MenuBookOutlined sx={{ fontSize: 22 }} aria-hidden />}
      title="Livro-caixa da clínica"
      meta={`${area.readAgentName} confere extratos e consulta o caixa`}
      actions={
        <>
          <Tooltip title="Conversas">
            <IconButton onClick={() => setHistoryOpen(true)} aria-label="Conversas" sx={{ width: 44, height: 44, color: colors.textPrimary }}><History /></IconButton>
          </Tooltip>
          <Tooltip title="Abrir o Livro-caixa">
            <IconButton onClick={() => openPageInNewTab("cash-book")} aria-label="Abrir o Livro-caixa" sx={{ width: 44, height: 44, color: colors.textPrimary }}><OpenInNew /></IconButton>
          </Tooltip>
        </>
      }
    />
  ) : (
    <AreaHeader
      avatar={<MenuBookOutlined aria-hidden />}
      title="Livro-caixa da clínica"
      meta={<>{area.readAgentName} confere extratos e consulta o caixa · <HeaderLink onClick={() => openPageInNewTab("cash-book")}>Abrir o Livro-caixa</HeaderLink></>}
      badge={<HeaderBadge>O extrato não fica guardado · nada é lançado sem você</HeaderBadge>}
    />
  );

  const renderItem = (it: Item, i: number) => {
    if (it.kind === "msg") {
      const isUser = it.role === "user";
      const isWriter = !isUser && !!it.agentId && it.agentId !== area.readAgentId;
      const { text, actions } = isUser ? { text: it.content, actions: [] as string[] } : parseAssistant(it.content);
      return (
        <Box key={`m${i}`}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SpeakerLabel isUser={isUser} initial={userInitial} name={isUser ? "Você" : it.agentName || area.readAgentName} />
            {isWriter && <WriterTag />}
          </Box>
          <Box sx={{ fontSize: "0.9375rem", lineHeight: 1.55, color: colors.textStrong, wordBreak: "break-word", whiteSpace: isUser ? "pre-wrap" : "normal" }}>
            {isUser ? <Box className="c4-text">{text}</Box> : renderMarkdown(text)}
          </Box>
          {i === items.length - 1 && <ActionChips actions={actions} onPick={(a) => send(a)} isMobile={isMobile} disabled={sending} />}
          {(it.proposals ?? []).length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
              {(it.proposals ?? []).map((card) => (
                <ProposalCard key={card.id} card={card} isMobile={isMobile} busy={busyCard === card.id} error={cardErrors[card.id]}
                  onConfirm={() => decide(card, "confirm")} onDiscard={() => decide(card, "discard")} onUndo={() => decide(card, "undo")}
                  onOpenCashBook={() => openPageInNewTab("cash-book")} />
              ))}
            </Box>
          )}
        </Box>
      );
    }
    if (it.kind === "file") {
      const ext = (it.name.split(".").pop() || "").toUpperCase().slice(0, 4);
      return (
        <Box key={`f${it.id}`}>
          <SpeakerLabel isUser initial={userInitial} name="Você" />
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.25, maxWidth: "100%", border: `1px solid ${colors.borderLight}`, borderRadius: "8px", px: 1.5, py: 1, bgcolor: colors.background }}>
            <Box aria-hidden sx={{ width: 32, height: 32, borderRadius: "6px", flexShrink: 0, bgcolor: ext === "PDF" ? colors.errorSoft : colors.successSoft,
              color: ext === "PDF" ? "#a71d2a" : "#1e6b31", fontSize: "0.5625rem", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{ext}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: colors.textStrong, overflowWrap: "anywhere" }}>{it.name}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>{sizeLabel(it.size)}</Typography>
            </Box>
          </Box>
        </Box>
      );
    }
    if (it.kind === "pick") {
      return (
        <Box key={`p${it.id}`}>
          <SpeakerLabel isUser={false} initial={userInitial} name={area.readAgentName} />
          <Box component="fieldset" sx={{ border: `1px solid ${colors.border}`, borderRadius: "8px", m: 0, p: 2, maxWidth: 520, display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography component="legend" sx={{ px: 0.5, fontSize: "0.875rem", fontWeight: 600, color: colors.textStrong }}>De qual conta é este extrato?</Typography>
            {accounts === null && <CircularProgress size={18} aria-label="Carregando as contas" />}
            {(accounts ?? []).map((a) => (
              <ButtonBase key={a.id} onClick={() => review(it.id, it.file, a, it.remember === true)}
                sx={{ minHeight: 44, px: 1.5, justifyContent: "flex-start", textAlign: "left", borderRadius: "8px", fontFamily: "inherit", fontSize: "0.875rem",
                  border: `1px solid ${colors.borderLight}`,
                  bgcolor: colors.white, color: colors.textStrong, fontWeight: 400,
                  "&:hover": { bgcolor: colors.primaryLight }, "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2 } }}>
                <Box sx={{ minWidth: 0 }}>
                  {a.title}
                  {a.description && <Box component="span" sx={{ display: "block", fontSize: "0.75rem", fontWeight: 400, color: colors.textSecondary }}>{a.description}</Box>}
                </Box>
              </ButtonBase>
            ))}
            {/* RN-575: escolher a conta não ensina nada; só com a marcação a escolha vale para os próximos extratos iguais */}
            <FormControlLabel
              control={<Checkbox size="small" checked={it.remember === true} onChange={(e) => replace(it.id, { ...it, remember: e.target.checked })} />}
              label="Lembrar esta conta para os próximos extratos como este"
              sx={{ mt: 0.5, mx: 0, minHeight: 44, "& .MuiFormControlLabel-label": { fontSize: "0.8125rem", color: colors.textPrimary } }} />
            <ButtonBase onClick={() => { replace(it.id, null); replace(-it.id, null); }} sx={{ alignSelf: "flex-start", minHeight: 36, px: 0.5, fontFamily: "inherit", fontSize: "0.8125rem", color: colors.textSecondary, textDecoration: "underline", textUnderlineOffset: "3px" }}>
              Cancelar
            </ButtonBase>
          </Box>
        </Box>
      );
    }
    return (
      <Box key={`s${it.id}`}>
        <StatementCard state={it.state} isMobile={isMobile}
          onChangeAccount={it.file && it.state.kind === "review" ? () => replace(it.id, { kind: "pick", id: it.id, file: it.file! }) : undefined} />
      </Box>
    );
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: colors.white }}>
      {header}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
      {isMobile ? (
        <Drawer anchor="left" open={historyOpen} onClose={() => setHistoryOpen(false)} disablePortal PaperProps={{ sx: { width: "85vw", maxWidth: 340 } }}>
          {history}
        </Drawer>
      ) : (
        <Box component="aside" aria-label="Conversas do Livro-caixa" sx={listColumnSx}>
          {history}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <AuditNotice clinicName={user?.clinicName} />
      <Box ref={scrollRef}
        onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDragging(true); } }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onFiles(e.dataTransfer.files); }}
        sx={{ flexGrow: 1, overflow: "auto", px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 }, position: "relative",
          outline: dragging ? `2px dashed ${colors.primary}` : "none", outlineOffset: -8, bgcolor: dragging ? colors.primaryLight : "transparent" }}>
        <Box role="log" aria-live="polite" aria-relevant="additions" aria-label="Conversa sobre o Livro-caixa"
          sx={{ display: "flex", flexDirection: "column", gap: 2.5, ...conversationSx }}>
          {items.length === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 600, color: colors.textStrong }}>O que você quer fazer no caixa?</Typography>
              <TopicList topics={topics} isMobile={isMobile} disabled={sending} />
              {!isMobile && <Typography sx={{ fontSize: "0.8125rem", color: colors.textSecondary }}>Você também pode arrastar o arquivo do extrato para cá.</Typography>}
            </Box>
          )}
          {items.map(renderItem)}
          {sending && (
            <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1, color: colors.textSecondary, fontSize: "0.8125rem" }}>
              <CircularProgress size={14} aria-hidden /> consultando…
            </Box>
          )}
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        </Box>
      </Box>

      <Box sx={{ flexShrink: 0, px: { xs: 1.5, md: 4 }, pt: 1, pb: { xs: 1, md: 1.75 }, borderTop: isMobile ? `1px solid ${colors.backgroundAlt}` : "none" }}>
        <Box>
          <input ref={fileRef} type="file" accept={ACCEPT} hidden onChange={(e) => onFiles(e.target.files)} />
          <ComposerFrame isMobile={isMobile}>
            <ComposerIconButton label="Anexar extrato" title="Anexar extrato (PDF, CSV ou Excel)" onClick={openFilePicker} isMobile={isMobile}>
              <AttachFile />
            </ComposerIconButton>
            <InputBase fullWidth multiline minRows={isMobile ? 1 : 2} maxRows={6} value={draft}
              placeholder={isMobile ? "Extrato ou pergunta…" : "Envie o extrato (PDF, CSV ou Excel) ou pergunte sobre o caixa…"}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              inputProps={{ "aria-label": "Mensagem para o Livro-caixa", maxLength: 4000 }}
              sx={{ fontSize: "0.9375rem", lineHeight: 1.45, color: colors.textStrong, py: 0.5, "& textarea::placeholder": { color: colors.textSecondary, opacity: 1 } }} />
            {voiceReady && dictation.supported && (
              <ComposerIconButton label={conversation ? "Sair do modo conversa" : "Modo conversa"} title={conversation ? "Sair do modo conversa" : "Modo conversa: fale e ouça a resposta"}
                onClick={toggleConversation} isMobile={isMobile} pressed={conversation} tone="voice">
                <HeadsetMic />
              </ComposerIconButton>
            )}
            {dictation.supported && (
              <ComposerIconButton label={speech.speaking ? "Interromper e falar" : dictation.listening ? "Parar o ditado" : "Ditar"}
                onClick={handleMic} isMobile={isMobile} pressed={dictation.listening} tone="recording">
                <Mic />
              </ComposerIconButton>
            )}
            <SendButton onClick={() => send()} disabled={sending || !draft.trim()} isMobile={isMobile} />
          </ComposerFrame>
          {conversation && (
            <VoiceStatus speaking={speech.speaking} sending={sending} transcribing={dictation.transcribing} listening={dictation.listening} />
          )}
          <Box sx={{ textAlign: "left", "& p": { textAlign: "left" } }}>
            <AssistantNote>
              {isMobile ? "Extrato lido na hora e descartado." : "Extrato em PDF, CSV ou Excel (.xlsx), até 10 MB. Lido na hora e descartado; nada é lançado sem você."}
            </AssistantNote>
          </Box>
        </Box>
      </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default CashBookAssistant;
